import { CopyableTextPreview, FormWizardTemplate, LoadingComponent, Stage, StyledCustomDialog, FormWizardTitle, QRCodeDisplay } from "@iventis/components";
import { Body2, Body3, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { createInitialState, findNode, TreeBrowserDomainNodeContext, useTreeBrowser, useTreeBrowserInitialise } from "@iventis/tree-browser";
import { toast } from "@iventis/toasts/src/toast";
import { ExtractRefFromComponent } from "@iventis/types/useful.types";
import React, { FunctionComponent, useState, useRef, forwardRef, useImperativeHandle, useMemo, useEffect } from "react";
import { Alert, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { SharedLinkExpiryDuration } from "@iventis/domain-model/model/sharedLinkExpiryDuration";
import { useWindowSize, canEdit, API_KEY_QUERY_PARAM_KEY, PROJECT_ID_QUERY_PARAM_KEY, useFunctionality } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { useProjectLimiter } from "@iventis/permissions/src/project-limiter-hook";
import { useProject } from "@iventis/permissions/src/use-project";
import { mappingApi } from "@iventis/api/src/api";
import EnumToRadioButtons from "@iventis/components/src/enum-to-radio-buttons";
import { NoAccessBanner } from "@iventis/permissions/src/upgrade-limit-overlay";
import { EmbeddedLibraryComponent } from "./plan-library/embedded-library";
import { libraryNodeServices } from "./services/library-api-functions";
import { libraryWebSockets } from "./plan-library/library.machine.web-sockets";
import { RootState } from "./state/root.store";
import { getSpatialLibraries } from "./services/libraries";
import { getSort } from "./plan-library/library-get-initial-values";
import { SharedLinkExpiryDurationToDays } from "./share-map-dialog.helpers";

enum MapShareOption {
    TEAM,
    LINK,
}

export const ShareMapDialog: FunctionComponent<{ isPersonalLibrary: boolean; isOpen: boolean; onClose: () => void }> = ({ isPersonalLibrary, isOpen, onClose }) => {
    const translate = useIventisTranslate();
    const { screenWidth } = useWindowSize();

    const [canUse] = useProjectLimiter();

    const moveMapRef = useRef<ExtractRefFromComponent<typeof MoveMapToSharedLibraryContainer>>(null);

    const [{ mainNodeId, tree }] = useTreeBrowser(TreeBrowserDomainNodeContext, null, "mainNodeId", "tree");

    const mapNode = useMemo(() => tree && findNode(tree?.childNodes, mainNodeId), [mainNodeId, tree]);

    const functionality = useFunctionality();

    const [mapShareOption, setMapShareOption] = useState<MapShareOption>(null);

    const [showCustomExpiry, setShowCustomExpiry] = useState(functionality.customSharedLinkExpiry);
    const [expiryTimeOption, setExpiryTimeOption] = useState<SharedLinkExpiryDuration>(SharedLinkExpiryDuration.SevenDays);

    const [stage, setStage] = useState(0);

    const { projectId } = useSelector((state: RootState) => state.auth.user);

    const { sharedLinks } = useSelector((state: RootState) => state.auth.subscriptionPlan.functionality);

    const theme = useTheme<Theme>();

    useEffect(() => {
        if (isOpen) {
            setStage(0);
            setMapShareOption(null);
            setExpiryTimeOption(SharedLinkExpiryDuration.SevenDays);
        }
    }, [isOpen]);

    const { component, primaryText, secondaryText, primaryCallback, isValid } = useMemo(() => {
        switch (mapShareOption) {
            case MapShareOption.TEAM:
                return {
                    component: <MoveMapToSharedLibraryContainer ref={moveMapRef} mapNode={mapNode} />,
                    primaryText: translate(Content.map5.move.moveTo),
                    secondaryText: [{ buttonText: translate(Content.common.buttons.back), onButtonPressed: () => setStage((s) => s - 1) }],
                    primaryCallback: canUse("teamMaps")
                        ? () => {
                              moveMapRef.current?.moveMap();
                              onClose();
                          }
                        : () => {
                              onClose();
                              functionality.open("sharedWorkspace");
                          },
                    isValid: canUse("teamMaps"),
                };
            case MapShareOption.LINK: {
                if (showCustomExpiry) {
                    return {
                        secondaryText: [{ buttonText: translate(Content.common.buttons.back), onButtonPressed: () => setStage((s) => s - 1) }],
                        primaryText: translate(Content.common.buttons.next),
                        primaryCallback: () => {
                            setShowCustomExpiry(false);
                            if (mapShareOption === MapShareOption.LINK) {
                                incrementMonitoringCount({ name: MetricName.PLAN_SHARED, resourceId: mapNode?.id });
                                incrementMonitoringCount({ name: `MAP_SHARED_LINK_EXPIRY_${expiryTimeOption.toUpperCase()}` as MetricName, resourceId: mapNode?.id });
                            }
                        },
                        isValid: expiryTimeOption != null,
                        component: (
                            <div>
                                {!functionality.customSharedLinkExpiry && <StyledNoAccessBanner upgradeType="customSharedLinkExpiry" className="no-access-banner" />}
                                <Body2>{translate(Content.map10.shareMap.customSharedLinkDuration.description)}</Body2>
                                <EnumToRadioButtons<SharedLinkExpiryDuration>
                                    radioButtons={Object.values(SharedLinkExpiryDuration).map((duration) => ({
                                        name: translate(Content.map10.shareMap.customSharedLinkDuration.duration[duration]),
                                        type: duration,
                                        testId: `expiry-days-${SharedLinkExpiryDurationToDays(duration)}`,
                                        disabled: !functionality.customSharedLinkExpiry,
                                    }))}
                                    value={expiryTimeOption}
                                    onChange={(value) => setExpiryTimeOption(value)}
                                    columns={2}
                                />
                                <Body3>{translate(Content.map10.shareMap.customSharedLinkDuration.contactSupport)}</Body3>
                            </div>
                        ),
                    };
                }
                return {
                    component: <GenerateLinkComponent mapId={mapNode.id} mapName={mapNode.name} projectId={projectId} expiryTimeOption={expiryTimeOption} />,
                    primaryText: translate(Content.common.buttons.close),
                    secondaryText: [
                        {
                            buttonText: translate(Content.common.buttons.back),
                            onButtonPressed: () => {
                                setShowCustomExpiry(true);
                            },
                        },
                    ],
                    primaryCallback: sharedLinks
                        ? () => {
                              onClose();
                          }
                        : () => {
                              onClose();
                              functionality.open("sharedLinks");
                          },
                    isValid: sharedLinks,
                };
            }
            default:
                return {
                    isValid: false,
                    component: <></>,
                    primaryText: undefined,
                    secondaryText: undefined,
                    primaryCallback: () => undefined,
                };
        }
    }, [mapShareOption, showCustomExpiry, expiryTimeOption, translate, sharedLinks, canUse("teamMaps")]);

    const stages: Stage[] = [
        {
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.close), onButtonPressed: onClose }],
            primaryButtonText: translate(Content.common.buttons.next),
            primaryButtonCallback: isValid
                ? () => {
                      if (mapShareOption === MapShareOption.LINK) {
                          setShowCustomExpiry(true);
                      }
                      setStage(1);
                  }
                : primaryCallback,
            isValid: mapShareOption != null,
            Component: (
                <div>
                    <EnumToRadioButtons<MapShareOption>
                        radioButtons={[
                            {
                                name: translate(Content.map6.shareMap.share_with_team),
                                description: translate(Content.map6.shareMap.share_with_team_details),
                                type: MapShareOption.TEAM,
                                disabled: !isPersonalLibrary || !canEdit(mapNode?.authorisation),
                                icon: canUse("teamMaps") ? undefined : <FontAwesomeIcon icon={["far", "circle-arrow-up"]} color={theme.typographyColors.primary} size="sm" />,
                                testId: "share-to-team-workspace",
                            },

                            {
                                name: translate(Content.map6.shareMap.share_link),
                                description: translate(Content.map6.shareMap.share_link_details),
                                type: MapShareOption.LINK,
                                disabled: !canEdit(mapNode?.authorisation),
                                icon: sharedLinks ? undefined : <FontAwesomeIcon icon={["far", "circle-arrow-up"]} color={theme.typographyColors.primary} size="sm" />,
                                testId: "generate-link",
                            },
                        ]}
                        value={mapShareOption}
                        onChange={(value) => setMapShareOption(value)}
                    />
                </div>
            ),
        },
        ...(isValid
            ? [
                  {
                      primaryButtonText: primaryText,
                      primaryButtonCallback: primaryCallback,
                      secondaryButtons: secondaryText,
                      Component: component,
                  },
              ]
            : [
                  {
                      primaryButtonText: undefined,
                      primaryButtonCallback: () => undefined,
                      Component: <></>,
                      secondaryButtons: [],
                  },
              ]),
    ];
    return (
        <StyledCustomDialog
            $noPadding={stage === 1 && !showCustomExpiry}
            $minWidth={stage === 1 && mapShareOption === MapShareOption.TEAM ? undefined : "520px"}
            $screenWidth={screenWidth}
            $staticHeight={stage === 1 && mapShareOption === MapShareOption.TEAM ? "80vh" : undefined}
            open={isOpen}
            onClose={onClose}
            data-testid="library-share-modal"
        >
            <FormWizardTemplate
                title={
                    (stage === 0 && <FormWizardTitle title={[<FontAwesomeIcon icon="link" />, translate(Content.map6.shareMap.title)]} />) ||
                    (stage === 1 && mapShareOption === MapShareOption.TEAM && (
                        <FormWizardTitle
                            title={[<FontAwesomeIcon icon="link" />, translate(Content.map6.shareMap.shared_library.title)]}
                            footer={translate(Content.map6.shareMap.shared_library.footer)}
                        />
                    )) ||
                    (stage === 1 && mapShareOption === MapShareOption.LINK && (
                        <FormWizardTitle title={[<FontAwesomeIcon icon="link" />, translate(Content.map6.shareMap.shared_link.title)]} />
                    ))
                }
                currentStage={stage}
                stages={stages}
            />
        </StyledCustomDialog>
    );
};

export const MoveMapToSharedLibraryContainer = forwardRef<{ moveMap(): void }, { mapNode: Node }>(({ mapNode }, ref) => {
    const { data: libraryNodes } = useQuery(["libraryNodes"], async () => getSpatialLibraries(), {
        initialData: [],
    });

    const sharedLib = useMemo(() => libraryNodes.find((n) => n.type === NodeType.MappingLibrary), [libraryNodes]);

    return sharedLib && <MoveMapToSharedLibrary ref={ref} sharedLib={sharedLib} mapNode={mapNode} />;
});

export const MoveMapToSharedLibrary = forwardRef<{ moveMap(): void }, { sharedLib: Node; mapNode: Node }>(({ sharedLib, mapNode }, ref) => {
    const translate = useIventisTranslate();

    const userId = useSelector((s: RootState) => s.auth.user.userId);

    const [, treeBrowserMainContext] = useTreeBrowser(TreeBrowserDomainNodeContext, null);

    const [, treeBrowserModalContext] = useTreeBrowserInitialise(
        createInitialState<Node>({ sort: getSort(userId), treeId: sharedLib?.id, singleSelectionOnly: true, selectedNodeIds: [sharedLib?.id] }),
        libraryNodeServices,
        libraryWebSockets
    );
    useImperativeHandle(
        ref,
        () => ({
            moveMap: () => {
                const moveToId = treeBrowserModalContext.currentState.selectedNodeIds?.[0] ?? treeBrowserModalContext.currentState.mainNodeId;
                // Move it within this modal context
                treeBrowserModalContext.moveNodes([mapNode], moveToId);
                // Replace the tree with this tree in the main context
                treeBrowserMainContext.setWithExistingTree({ tree: treeBrowserModalContext.currentState.tree, mainNodeId: mapNode.id });
            },
        }),
        []
    );

    const canSelectNode = (node: Node) => {
        if (node.type !== NodeType.Folder) {
            return { disabled: true, tooltip: translate(Content.map5.move.tooltip[node.type]) };
        }
        if (!canEdit(node.authorisation)) {
            return { disabled: true };
        }
        return { disabled: false, tooltip: undefined };
    };

    return (
        <TreeBrowserDomainNodeContext.Provider value={treeBrowserModalContext}>
            {treeBrowserModalContext.currentState.isLoadingTree ? (
                <LoadingComponent />
            ) : (
                <StyledLibrary canSelectNode={canSelectNode} canSwitchLibrary={false} libraries={[sharedLib]} userId={userId} />
            )}
        </TreeBrowserDomainNodeContext.Provider>
    );
});

const GenerateLinkComponent: React.FunctionComponent<{ mapId: string; mapName: string; projectId: string; expiryTimeOption: SharedLinkExpiryDuration }> = ({
    mapId,
    mapName,
    projectId,
    expiryTimeOption,
}) => {
    const translate = useIventisTranslate();
    const project = useProject();

    // Create our network request to get our shared link
    const { data: apiKey } = useQuery(
        ["mapId", mapId, "expiryTimeOption", expiryTimeOption],
        async () =>
            (
                await mappingApi.post<string>(`map-share-links/${mapId}`, expiryTimeOption, {
                    headers: { "Content-Type": "application/json" },
                })
            ).data
    );

    const link = useMemo(() => {
        if (apiKey == null) return undefined;
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.append(API_KEY_QUERY_PARAM_KEY, apiKey);
        queryParams.append(PROJECT_ID_QUERY_PARAM_KEY, projectId);
        return `${window.location.origin}${window.location.pathname}/?${queryParams.toString()}${window.location.hash}`;
    }, [apiKey]);

    return (
        <GenerateLinkContainer>
            <span>
                <Body2>{translate(Content.common.link)}</Body2>
                <StyledCopyableTextPreview
                    fieldLabel=""
                    text={link}
                    onSuccessfulCopy={() => toast.success({ title: translate(Content.common.copied_to_clipboard), message: translate(Content.map10.shareMap.copyNotification) })}
                    testId="generated-link"
                />
            </span>
            {link && (
                <StyledQRCodeDisplay
                    value={link}
                    size={1000}
                    expiryDate={new Date(Date.now() + SharedLinkExpiryDurationToDays(expiryTimeOption) * 24 * 60 * 60 * 1000)}
                    title={mapName}
                    logoUrl={project?.logoUrl}
                    data-testid="qr-code"
                    onCopyCompleted={() =>
                        toast.success({
                            title: translate(Content.common.copied_to_clipboard),
                            message: translate(Content.map11.qrCode.copySuccess),
                            testId: "qr-code-copy-success-toast",
                        })
                    }
                    onCopyError={() =>
                        toast.error({
                            title: translate(Content.map11.qrCode.copyError.title),
                            message: translate(Content.map11.qrCode.copyError.message),
                            testId: "qr-code-copy-error-toast",
                        })
                    }
                    onDownloadCompleted={() =>
                        toast.success({
                            title: translate(Content.map11.qrCode.downloadSuccess.title),
                            message: translate(Content.map11.qrCode.downloadSuccess.message),
                            testId: "qr-code-download-success-toast",
                        })
                    }
                    onDownloadError={() =>
                        toast.error({
                            title: translate(Content.map11.qrCode.downloadError.title),
                            message: translate(Content.map11.qrCode.downloadError.message),
                            testId: "qr-code-download-error-toast",
                        })
                    }
                />
            )}
            <Body2 data-testid="link-expiry-length-message">
                {translate(Content.map6.shareMap.share_link_generated, { numberOfDays: SharedLinkExpiryDurationToDays(expiryTimeOption) })}
            </Body2>
            <Alert severity="warning">
                <Body2>{translate(Content.map10.shareMap.useDescription)}</Body2>
                <br />
                <Body3>{translate(Content.map10.shareMap.warning)}</Body3>
            </Alert>
        </GenerateLinkContainer>
    );
};

const StyledLibrary = styled(EmbeddedLibraryComponent)`
    min-height: 80vh;
`;

const GenerateLinkContainer = styled.div`
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 600px;
`;

const StyledCopyableTextPreview = styled(CopyableTextPreview)`
    width: 100%;
`;

const StyledQRCodeDisplay = styled(QRCodeDisplay)`
    margin-top: 8px;
    width: 50%;
    margin: auto;
`;

const StyledNoAccessBanner = styled(NoAccessBanner)`
    margin-bottom: 10px;
`;
